import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Pencil, Download, Plus, Upload, FileText, Trash2, FolderPlus, ExternalLink, Eye, X, Lock, Link as LinkIcon, Copy, Check, Mail, MessageCircle, RotateCcw } from "lucide-react";
import Shell, { Breadcrumbs } from "@/components/Shell";
import { Card, Btn, Select, Spinner, StatusBadge, IconBtn, Input, Label } from "@/components/ui-kit";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import TaskItem from "@/components/TaskItem";
import TaskModal from "@/components/TaskModal";
import PdfViewer from "@/components/PdfViewer";
import api from "@/lib/api";
import { API } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useConfirm } from "@/lib/confirm";
import { downloadFromUrl } from "@/lib/download";
import { QUOTE_STATUSES, STATUS_BG, formatGBP, formatDateUK } from "@/lib/format";

const newCostRow = () => ({
  id: `tmp-${Math.random().toString(36).slice(2, 9)}`,
  supplier_id: "",
  description: "",
  amount: "",
});

const QuoteDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { user } = useAuth();
  const [q, setQ] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [previewPdf, setPreviewPdf] = useState(null); // {url, name}
  const fileRef = useRef(null);
  const [suppliers, setSuppliers] = useState([]);
  const [costs, setCosts] = useState([]); // [{id, supplier_id, description, amount}]
  const [costsDirty, setCostsDirty] = useState(false);
  const [savingCosts, setSavingCosts] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [contactEmails, setContactEmails] = useState([]); // [{name,email}]

  useEffect(() => { api.get("/suppliers").then((r) => setSuppliers(r.data)); }, []);

  // Cleanup blob URL when preview closes/changes
  useEffect(() => {
    return () => { if (previewPdf?.url) window.URL.revokeObjectURL(previewPdf.url); };
  }, [previewPdf]);

  const previewSupplier = async (pdf) => {
    const res = await api.get(`/quotes/${id}/supplier-pdfs/${pdf.id}/download`, { responseType: "blob" });
    const blob = new Blob([res.data], { type: "application/pdf" });
    const url = window.URL.createObjectURL(blob);
    setPreviewPdf({ url, name: pdf.original_name });
  };

  const closePreview = () => {
    if (previewPdf?.url) window.URL.revokeObjectURL(previewPdf.url);
    setPreviewPdf(null);
  };

  const load = async () => {
    const { data } = await api.get(`/quotes/${id}`);
    setQ(data);
    setCosts((data.internal_costs || []).map((c) => ({
      id: c.id || `tmp-${Math.random().toString(36).slice(2, 9)}`,
      supplier_id: c.supplier_id || "",
      description: c.description || "",
      amount: c.amount === 0 ? "" : (c.amount ?? ""),
    })));
    setCostsDirty(false);
  };
  useEffect(() => { load(); }, [id]);

  // Load client contacts so the "Email link" button can pre-fill the recipient(s).
  useEffect(() => {
    if (!q?.client_id) { setContactEmails([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/clients/${q.client_id}`);
        if (cancelled) return;
        const out = [];
        (data.contacts || []).forEach((c) => {
          const e = (c.email || "").trim();
          if (e) out.push({ name: c.name || e, email: e });
        });
        const ce = (data.email || "").trim();
        if (ce && !out.some((x) => x.email.toLowerCase() === ce.toLowerCase())) {
          out.unshift({ name: data.name || ce, email: ce });
        }
        setContactEmails(out);
      } catch { setContactEmails([]); }
    })();
    return () => { cancelled = true; };
  }, [q?.client_id]);

  // Mark the client's latest selection as "seen" by the designer when the page mounts.
  useEffect(() => {
    if (!q?.id || !q?.client_selected_at) return;
    if (q.client_seen_at && q.client_seen_at >= q.client_selected_at) return;
    api.post(`/quotes/${q.id}/mark-selection-seen`)
      .then(() => window.dispatchEvent(new Event("quotes:unseen-refresh")))
      .catch(() => {});
  }, [q?.id, q?.client_selected_at, q?.client_seen_at]);

  const shareUrl = useMemo(() => {
    if (!q?.share_token) return "";
    const slugify = (s) => (s || "")
      .toLowerCase()
      .replace(/['"`]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
    const parts = [];
    if (q.quote_number) parts.push(`q${q.quote_number}`);
    if (q.client_name) parts.push(q.client_name);
    if (q.name) parts.push(q.name);
    const slug = slugify(parts.join(" ")).slice(0, 80).replace(/-+$/, "");
    return `${window.location.origin}/quote/${q.share_token}${slug ? `/${slug}` : ""}`;
  }, [q?.share_token, q?.quote_number, q?.client_name, q?.name]);

  const copyShareLink = async () => {
    if (!shareUrl) return;
    try { await navigator.clipboard.writeText(shareUrl); }
    catch {
      const ta = document.createElement("textarea");
      ta.value = shareUrl; document.body.appendChild(ta); ta.select(); document.execCommand("copy"); ta.remove();
    }
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 1500);
  };

  const buildQuoteMailto = (recipient) => {
    const firstName = recipient?.name ? recipient.name.split(/\s+/)[0] : "there";
    const designer = user?.name || "Kerboom Design";
    const subject = `Your quote from Kerboom Design — Q${q.quote_number}`;
    const body = [
      `Hi ${firstName},`,
      "",
      `Please find your quote for ${q.name} attached. You can also review and choose your preferred options online at the link below — tick what you'd like to go ahead with and hit Send, and I'll be notified straight away.`,
      "",
      `Review & select your options: ${shareUrl}`,
      "",
      "Let me know if you have any questions.",
      "",
      "Thanks,",
      designer,
    ].join("\n");
    const to = recipient?.email ? encodeURIComponent(recipient.email) : "";
    return `mailto:${to}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const openMail = (recipient) => { window.location.href = buildQuoteMailto(recipient); };

  // Clear the client's previous submission so they can submit fresh against a
  // revised quote. Reverts status from "Client Selected" → "Sent".
  const resetClientSelection = async () => {
    const ok = await confirm({
      title: "Reset client's selection?",
      description: "This clears the client's previous picks, name, and comment so they see a fresh review page next time. The quote status will revert to Sent. The quote itself, tasks and prices stay exactly as they are. Continue?",
      confirmText: "Reset selection",
      destructive: true,
    });
    if (!ok) return;
    await api.post(`/quotes/${id}/reset-selection`);
    await load();
    window.dispatchEvent(new Event("quotes:unseen-refresh"));
  };

  const updateCost = (idx, key, value) => {
    setCosts((rows) => rows.map((r, i) => (i === idx ? { ...r, [key]: value } : r)));
    setCostsDirty(true);
  };
  const addCost = () => { setCosts((r) => [...r, newCostRow()]); setCostsDirty(true); };
  const removeCost = (idx) => {
    setCosts((r) => r.filter((_, i) => i !== idx));
    setCostsDirty(true);
  };
  const saveCosts = async () => {
    setSavingCosts(true);
    try {
      const payload = {
        items: costs.map((c) => ({
          id: c.id?.startsWith("tmp-") ? null : c.id,
          supplier_id: c.supplier_id || "",
          description: c.description || "",
          amount: parseFloat(c.amount || 0),
        })),
      };
      await api.put(`/quotes/${id}/internal-costs`, payload);
      await load();
    } finally {
      setSavingCosts(false);
    }
  };

  const internalTotal = costs.reduce((sum, c) => sum + (parseFloat(c.amount) || 0), 0);

  const updateStatus = async (status) => {
    await api.patch(`/quotes/${id}/status`, { status });
    load();
  };

  const saveTask = async (payload) => {
    if (editingTask) {
      await api.put(`/quotes/${id}/tasks/${editingTask.id}`, payload);
    } else {
      await api.post(`/quotes/${id}/tasks`, payload);
    }
    setEditingTask(null);
    load();
  };

  const delTask = async (tid) => {
    if (!(await confirm({ title: "Delete this task?", description: "This cannot be undone.", confirmText: "Delete", destructive: true }))) return;
    await api.delete(`/quotes/${id}/tasks/${tid}`);
    load();
  };

  const duplicateTask = async (tid) => {
    await api.post(`/quotes/${id}/tasks/${tid}/duplicate`);
    load();
  };

  const toggleChosen = async (tid) => {
    await api.post(`/quotes/${id}/tasks/${tid}/toggle-chosen`);
    load();
  };

  const moveTask = async (tid, direction) => {
    const tasks = q?.tasks || [];
    const idx = tasks.findIndex((t) => t.id === tid);
    if (idx < 0) return;
    const newIdx = direction === "up" ? idx - 1 : idx + 1;
    if (newIdx < 0 || newIdx >= tasks.length) return;
    const reordered = [...tasks];
    [reordered[idx], reordered[newIdx]] = [reordered[newIdx], reordered[idx]];
    // Optimistic update
    setQ({ ...q, tasks: reordered });
    try {
      await api.post(`/quotes/${id}/tasks/reorder`, { task_ids: reordered.map((t) => t.id) });
    } catch (e) {
      load(); // rollback by re-fetching
    }
  };

  const chosenSummary = (q?.tasks || []).reduce(
    (acc, t) => {
      if (!t.client_chosen) return acc;
      const v = t.cost_type === "range" ? (t.cost_max || 0) : (t.cost || 0);
      return { count: acc.count + 1, total: acc.total + (parseFloat(v) || 0) };
    },
    { count: 0, total: 0 }
  );

  const downloadPdf = () => {
    downloadFromUrl(`/quotes/${id}/pdf`);
  };

  const pdfHref = (() => {
    const token = (() => { try { return localStorage.getItem("kerboom_token") || ""; } catch { return ""; } })();
    return `${API}/quotes/${id}/pdf${token ? `?token=${encodeURIComponent(token)}` : ""}`;
  })();

  const onFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/quotes/${id}/supplier-pdfs`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      await load();
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const downloadSupplier = (pdf) => {
    downloadFromUrl(`/quotes/${id}/supplier-pdfs/${pdf.id}/download`);
  };

  const deleteSupplier = async (pdfId) => {
    if (!(await confirm({ title: "Delete this supplier PDF?", description: "The file will be permanently removed from this quote.", confirmText: "Delete", destructive: true }))) return;
    await api.delete(`/quotes/${id}/supplier-pdfs/${pdfId}`);
    load();
  };

  const makeProject = async () => {
    if (!(await confirm({ title: "Create a project from this quote?", description: "Tasks will be copied to a new project.", confirmText: "Create Project" }))) return;
    const { data } = await api.post(`/quotes/${id}/make-project`);
    navigate(`/projects/${data.id}`);
  };

  if (!q) return <Shell><div className="flex justify-center py-20"><Spinner /></div></Shell>;

  return (
    <Shell>
      <Breadcrumbs items={[{ to: "/quotes", label: "Quotes" }, { label: `Q${q.quote_number}` }]} />
      <div className="flex items-start justify-between gap-4 mb-8">
        <div>
          <h1 className="font-display text-4xl font-bold text-zinc-950">Q{q.quote_number}</h1>
          <p className="text-sm text-zinc-700 mt-1">{q.client_name} — {q.name}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          <span className="text-xs text-zinc-500 mr-1">Status:</span>
          <Select data-testid="quote-status-dropdown" value={q.status} onChange={(e) => updateStatus(e.target.value)} className={`!w-auto !py-2 ${STATUS_BG[q.status] || ""}`}>
            {QUOTE_STATUSES.map((s) => <option key={s}>{s}</option>)}
          </Select>
          <Btn as="a" href={pdfHref} target="_blank" rel="noopener" variant="secondary" data-testid="quote-pdf-btn"><Download className="w-4 h-4" />PDF</Btn>
          <Btn
            variant={linkCopied ? "primary" : "secondary"}
            onClick={copyShareLink}
            data-testid="quote-copy-link-btn"
            title="Copy the public selection link"
          >
            {linkCopied ? <><Check className="w-4 h-4" />Copied</> : <><LinkIcon className="w-4 h-4" />Copy link</>}
          </Btn>
          {contactEmails.length <= 1 ? (
            <Btn
              variant="secondary"
              onClick={() => openMail(contactEmails[0])}
              data-testid="quote-email-link-btn"
              title={contactEmails[0]?.email ? `Open new email to ${contactEmails[0].email}` : "Open new email (pick recipient in Mail)"}
            >
              <Mail className="w-4 h-4" />Email link
            </Btn>
          ) : (
            <Popover>
              <PopoverTrigger asChild>
                <Btn variant="secondary" data-testid="quote-email-link-btn"><Mail className="w-4 h-4" />Email link</Btn>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-72 p-1" data-testid="quote-email-recipients">
                <p className="px-3 py-2 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Send to</p>
                {contactEmails.map((c) => (
                  <button
                    key={c.email}
                    type="button"
                    onClick={() => openMail(c)}
                    className="w-full text-left px-3 py-2 rounded-md hover:bg-zinc-100 transition-colors"
                    data-testid={`quote-email-recipient-${c.email}`}
                  >
                    <div className="text-sm font-medium text-zinc-900 truncate">{c.name}</div>
                    <div className="text-[11px] text-zinc-500 truncate">{c.email}</div>
                  </button>
                ))}
                <div className="border-t border-zinc-100 my-1" />
                <button
                  type="button"
                  onClick={() => openMail(null)}
                  className="w-full text-left px-3 py-2 rounded-md hover:bg-zinc-100 text-xs text-zinc-600"
                >
                  Other recipient… (pick in Mail)
                </button>
              </PopoverContent>
            </Popover>
          )}
          {!q.linked_project && (
            <Btn variant="secondary" onClick={makeProject} data-testid="quote-make-project-btn"><FolderPlus className="w-4 h-4" />Make Project</Btn>
          )}
          <Btn variant="secondary" onClick={() => navigate(`/quotes/${id}/edit`)} data-testid="edit-quote-btn"><Pencil className="w-4 h-4" />Edit Quote</Btn>
        </div>
      </div>

      {/* Client selection banner — only when the client has submitted picks */}
      {q.client_selected_at && (
        <Card className="p-5 mb-6 border-emerald-300 bg-emerald-50/60" data-testid="quote-client-selection-banner">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-100 grid place-items-center shrink-0">
              <Check className="w-5 h-5 text-emerald-700" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <h3 className="font-display text-lg font-bold text-zinc-950">
                    Client selected {(q.tasks || []).filter((t) => t.client_chosen).length} of {(q.tasks || []).length} option{(q.tasks || []).length !== 1 ? "s" : ""}
                  </h3>
                  <p className="text-xs text-zinc-700 mt-0.5">
                    {q.client_confirmed_by ? (
                      <>Confirmed by <strong className="text-zinc-950">{q.client_confirmed_by}</strong> · </>
                    ) : null}
                    {new Date(q.client_selected_at).toLocaleString("en-GB")}
                    {q.client_seen_at && q.client_seen_at >= q.client_selected_at ? "" : " — new!"}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={resetClientSelection}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-white border border-emerald-300 text-emerald-800 hover:bg-emerald-100 transition-colors shrink-0"
                  data-testid="quote-reset-selection-btn"
                  title="Clear this submission so the client can submit fresh on a revised quote"
                >
                  <RotateCcw className="w-3 h-3" />Reset selection
                </button>
              </div>
              {q.client_selection_comment && (
                <div className="mt-3 rounded-lg bg-white border border-emerald-200 px-3 py-2 flex items-start gap-2" data-testid="quote-client-comment">
                  <MessageCircle className="w-4 h-4 text-emerald-700 mt-0.5 shrink-0" />
                  <p className="text-sm text-zinc-800 whitespace-pre-line">{q.client_selection_comment}</p>
                </div>
              )}
            </div>
          </div>
        </Card>
      )}

      <div className="grid md:grid-cols-2 gap-4 mb-6">
        <Card className="p-6">
          <h2 className="font-display text-lg font-semibold text-zinc-950 mb-4">Quote Details</h2>
          <dl className="space-y-3 text-sm">
            <div>
              <dt className="text-xs uppercase tracking-widest text-zinc-500 font-semibold">Client</dt>
              <dd className="text-zinc-950 mt-0.5"><Link to={`/clients/${q.client_id}`} className="hover:underline">{q.client_name}</Link></dd>
            </div>
            {q.contact_name && (
              <div>
                <dt className="text-xs uppercase tracking-widest text-zinc-500 font-semibold">Contact</dt>
                <dd className="text-zinc-950 mt-0.5">{q.contact_name}</dd>
              </div>
            )}
            <div>
              <dt className="text-xs uppercase tracking-widest text-zinc-500 font-semibold">Quote Date</dt>
              <dd className="text-zinc-950 mt-0.5">{formatDateUK(q.date)}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-widest text-zinc-500 font-semibold">Total</dt>
              <dd className="font-display text-2xl font-bold text-zinc-950 mt-0.5">{formatGBP(q.total)}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-widest text-zinc-500 font-semibold">Linked Project</dt>
              <dd className="mt-0.5">
                {q.linked_project ? (
                  <Link to={`/projects/${q.linked_project.id}`} className="inline-flex items-center gap-1 text-zinc-950 hover:underline">
                    <ExternalLink className="w-3.5 h-3.5" />
                    {q.linked_project.name} (#{q.linked_project.job_number})
                  </Link>
                ) : (
                  <span className="text-zinc-500 italic">Not linked</span>
                )}
              </dd>
            </div>
          </dl>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="font-display text-lg font-semibold text-zinc-950">Supplier PDFs</h2>
              <p className="text-xs text-zinc-500 mt-0.5">Upload supplier quotes you've received (e.g. print suppliers).</p>
            </div>
            <div>
              <input ref={fileRef} type="file" accept="application/pdf" onChange={onFileSelect} className="hidden" data-testid="supplier-pdf-input" />
              <Btn size="sm" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upload-supplier-btn">
                <Upload className="w-3.5 h-3.5" />{uploading ? "Uploading…" : "Upload PDF"}
              </Btn>
            </div>
          </div>
          {(!q.supplier_pdfs || q.supplier_pdfs.length === 0) ? (
            <p className="text-sm text-zinc-500 text-center py-6">No supplier PDFs uploaded</p>
          ) : (
            <div className="space-y-2">
              {q.supplier_pdfs.map((pdf) => (
                <div key={pdf.id} data-testid={`supplier-pdf-${pdf.id}`} className="flex items-center justify-between gap-2 p-3 border border-zinc-100 rounded-lg hover:bg-zinc-50 transition-colors">
                  <button
                    onClick={() => previewSupplier(pdf)}
                    className="flex items-center gap-2 text-sm text-zinc-950 hover:text-zinc-700 min-w-0 flex-1 text-left"
                    data-testid={`supplier-pdf-preview-${pdf.id}`}
                  >
                    <FileText className="w-4 h-4 text-zinc-400 shrink-0" />
                    <span className="truncate">{pdf.original_name}</span>
                  </button>
                  <div className="flex items-center gap-1 shrink-0">
                    <IconBtn onClick={() => previewSupplier(pdf)} title="Preview" data-testid={`supplier-pdf-view-btn-${pdf.id}`}>
                      <Eye className="w-4 h-4" />
                    </IconBtn>
                    <IconBtn onClick={() => downloadSupplier(pdf)} title="Download" data-testid={`supplier-pdf-download-${pdf.id}`}>
                      <Download className="w-4 h-4" />
                    </IconBtn>
                    <IconBtn variant="destructive" onClick={() => deleteSupplier(pdf.id)} data-testid={`supplier-pdf-delete-${pdf.id}`}><Trash2 className="w-4 h-4" /></IconBtn>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      <Card className="p-6 mb-6">
        <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
          <div className="flex items-center gap-3 flex-wrap">
            <h2 className="font-display text-lg font-semibold text-zinc-950">Tasks</h2>
            {chosenSummary.count > 0 && (
              <div
                data-testid="quote-chosen-summary"
                className="inline-flex items-center gap-1.5 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-full px-3 py-1 text-xs font-medium"
                title="These will be sent to Xero for invoicing"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                {chosenSummary.count} chosen · {formatGBP(chosenSummary.total)}
                <span className="text-[10px] text-emerald-700/70 hidden sm:inline">→ Xero</span>
              </div>
            )}
          </div>
          <Btn size="sm" onClick={() => { setEditingTask(null); setModalOpen(true); }} data-testid="add-task-btn">
            <Plus className="w-3.5 h-3.5" />Add Task
          </Btn>
        </div>
        {(!q.tasks || q.tasks.length === 0) ? (
          <p className="text-sm text-zinc-500 text-center py-8">No tasks yet — add tasks to build the quote total.</p>
        ) : (
          <div className="space-y-2">
            {q.tasks.map((t, idx) => (
              <TaskItem
                key={t.id}
                task={t}
                testIdPrefix="quote-task"
                onEdit={(task) => { setEditingTask(task); setModalOpen(true); }}
                onDelete={delTask}
                onDuplicate={duplicateTask}
                onToggleChosen={toggleChosen}
                onMoveUp={(tid) => moveTask(tid, "up")}
                onMoveDown={(tid) => moveTask(tid, "down")}
                canMoveUp={idx > 0}
                canMoveDown={idx < q.tasks.length - 1}
                showDuplicate
                showChosen
                showReorder
              />
            ))}
          </div>
        )}
      </Card>

      <Card className="p-6 border-amber-200 bg-amber-50/40" data-testid="internal-costs-card">
        <div className="flex items-start justify-between mb-1 gap-3 flex-wrap">
          <div>
            <h2 className="font-display text-lg font-semibold text-zinc-950 inline-flex items-center gap-2">
              <Lock className="w-4 h-4 text-amber-700" />
              Internal Supplier Costs
            </h2>
            <p className="text-xs text-zinc-600 mt-1">For your own records. <strong>Never shown on the client PDF.</strong></p>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <p className="text-[10px] text-zinc-500 uppercase tracking-wider">Internal total</p>
              <p className="font-display text-lg font-bold text-zinc-950" data-testid="internal-total">{formatGBP(internalTotal)}</p>
            </div>
            <Btn size="sm" disabled={!costsDirty || savingCosts} onClick={saveCosts} data-testid="internal-costs-save">
              {savingCosts ? "Saving…" : "Save costs"}
            </Btn>
          </div>
        </div>

        <div className="mt-4 space-y-2">
          {costs.length === 0 && (
            <p className="text-sm text-zinc-500 italic py-2">No internal costs added.</p>
          )}
          {costs.map((row, idx) => (
            <div key={row.id} data-testid={`internal-cost-row-${idx}`} className="grid grid-cols-12 gap-2 items-end p-3 bg-white border border-zinc-200 rounded-lg">
              <div className="col-span-12 md:col-span-4">
                <Label className="!text-[11px]">Supplier</Label>
                <Select
                  data-testid={`internal-cost-supplier-${idx}`}
                  value={row.supplier_id}
                  onChange={(e) => updateCost(idx, "supplier_id", e.target.value)}
                  className="!py-2"
                >
                  <option value="">— None —</option>
                  {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}{s.type ? ` · ${s.type}` : ""}</option>)}
                </Select>
              </div>
              <div className="col-span-8 md:col-span-5">
                <Label className="!text-[11px]">Cost type / description</Label>
                <Input
                  data-testid={`internal-cost-desc-${idx}`}
                  value={row.description}
                  onChange={(e) => updateCost(idx, "description", e.target.value)}
                  placeholder="e.g. Print 4-colour A6 + crease"
                  className="!py-2"
                />
              </div>
              <div className="col-span-3 md:col-span-2">
                <Label className="!text-[11px]">Amount (£)</Label>
                <Input
                  data-testid={`internal-cost-amount-${idx}`}
                  type="number"
                  step="0.01"
                  min="0"
                  value={row.amount}
                  onChange={(e) => updateCost(idx, "amount", e.target.value)}
                  className="!py-2"
                />
              </div>
              <div className="col-span-1 flex justify-end pb-0.5">
                <IconBtn variant="destructive" onClick={() => removeCost(idx)} data-testid={`internal-cost-remove-${idx}`} title="Remove">
                  <Trash2 className="w-4 h-4" />
                </IconBtn>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-3 flex items-center justify-between flex-wrap gap-2">
          <Btn size="sm" variant="secondary" onClick={addCost} data-testid="internal-cost-add">
            <Plus className="w-3.5 h-3.5" />Add cost
          </Btn>
          {suppliers.length === 0 && (
            <p className="text-xs text-zinc-500 italic">No suppliers yet — <a href="/suppliers/new" className="underline hover:text-zinc-900">add one</a> first.</p>
          )}
        </div>
      </Card>

      <TaskModal
        open={modalOpen}
        onClose={() => { setModalOpen(false); setEditingTask(null); }}
        onSave={saveTask}
        task={editingTask}
        title={editingTask ? "Edit Task" : "Add New Task"}
        subtitle={`${editingTask ? "Update" : "Add a task to"} ${q.name}`}
        allowRange
        suppliers={suppliers}
      />

      {previewPdf && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 animate-fade-in" data-testid="supplier-pdf-preview-modal">
          <div className="absolute inset-0 bg-zinc-950/70 backdrop-blur-sm" onClick={closePreview} />
          <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-5xl h-[90vh] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-zinc-200 shrink-0">
              <div className="flex items-center gap-2 min-w-0">
                <FileText className="w-4 h-4 text-zinc-500 shrink-0" />
                <p className="text-sm font-semibold text-zinc-950 truncate">{previewPdf.name}</p>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <a
                  href={previewPdf.url}
                  download={previewPdf.name}
                  data-testid="supplier-pdf-preview-download"
                  className="p-2 rounded-lg hover:bg-zinc-100 text-zinc-500 hover:text-zinc-900 transition-colors"
                  title="Download"
                >
                  <Download className="w-4 h-4" />
                </a>
                <button
                  onClick={closePreview}
                  data-testid="supplier-pdf-preview-close"
                  className="p-2 rounded-lg hover:bg-zinc-100 text-zinc-500 hover:text-zinc-900 transition-colors"
                  title="Close"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="flex-1 w-full overflow-hidden">
              <PdfViewer fileUrl={previewPdf.url} />
            </div>
          </div>
        </div>
      )}

    </Shell>
  );
};

export default QuoteDetail;
